/**
 * Print-Only Value Gap Details Page for PDF Generation
 * Renders comprehensive value gap analysis for PDF export
 *
 * URL: /print/value-gap-details/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';
import { useDateFormatter } from '../../utils/dateUtils';
import '../../print.css';

const PrintValueGapDetails = () => {
  const { businessId } = useParams();
  const { formatDate } = useDateFormatter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    businessProfile: null,
    valueGap: null,
    profitGap: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter or localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch data
        const [profileResponse, valueGapResponse, profitGapResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/value-gap`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/profit-gap`, { headers })
        ]);

        const profileData = profileResponse.ok ? await profileResponse.json() : null;
        const valueGapData = valueGapResponse.ok ? await valueGapResponse.json() : null;
        const profitGapData = profitGapResponse.ok ? await profitGapResponse.json() : null;

        setData({
          businessProfile: profileData?.data?.profile || null,
          valueGap: valueGapData?.value_gap || null,
          profitGap: profitGapData?.profit_gap || null
        });

        setLoading(false);
      } catch (err) {
        console.error('[PrintValueGapDetails] Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && !error) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title
      document.title = `Value Gap Analysis Report`;
    }
  }, [loading, error]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading value gap details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const businessName = data?.businessProfile?.business_name || 'Business Name Not Set';
  const industry = data?.businessProfile?.industry;
  const valueGap = data?.valueGap;
  const profitGap = data?.profitGap;

  if (!valueGap) {
    return (
      <div className="print-container" data-pdf-ready="true">
        <div className="print-page">No value gap data available</div>
      </div>
    );
  }

  // Calculate valuation matrix for all combinations (from ValuationProgressionModal)
  const calculateMatrix = () => {
    if (!valueGap || !profitGap) return null;

    const revenue = profitGap.current_revenue || 0;
    const currentEbitda = profitGap.current_ebitda || 1;
    const currentMargin = profitGap.current_ebitda_margin || 0;
    const medianMargin = profitGap.industry_median_ebitda_margin || 0;
    const bestMargin = profitGap.industry_high_ebitda_margin || 0;
    const ebitdaMultiple = profitGap.ebitda_multiple || 0;

    const currentScore = valueGap.current_attractiveness || 58;
    const greenZoneScore = 67;
    const bestScore = 72;

    // Get valuation range (these are dollar amounts at current EBITDA)
    const lowRange = valueGap.valuation_low || 0;
    const highRange = valueGap.valuation_high || 0;

    // Function to calculate valuation for a given EBITDA margin and assessment score
    const calculateValuation = (ebitdaMargin, assessmentScore) => {
      const newEbitda = revenue * (ebitdaMargin / 100);
      const scalingFactor = currentEbitda > 0 ? newEbitda / currentEbitda : 1;
      const newValLow = lowRange * scalingFactor;
      const newValHigh = highRange * scalingFactor;
      const newRange = newValHigh - newValLow;
      const position = Math.max(0, Math.min(100, (assessmentScore - 17) / 83 * 100));
      return newValLow + (newRange * position / 100);
    };

    // Calculate valuation ranges for each EBITDA margin scenario
    const calculateRangeForMargin = (ebitdaMargin) => {
      const newEbitda = revenue * (ebitdaMargin / 100);
      const scalingFactor = currentEbitda > 0 ? newEbitda / currentEbitda : 1;
      const newValLow = lowRange * scalingFactor;
      const newValHigh = highRange * scalingFactor;
      return { low: newValLow, high: newValHigh };
    };

    const currentMarginRange = calculateRangeForMargin(currentMargin);
    const medianMarginRange = calculateRangeForMargin(medianMargin);
    const bestMarginRange = calculateRangeForMargin(bestMargin);

    // Calculate all 9 matrix cells
    return {
      currentScore_currentMargin: calculateValuation(currentMargin, currentScore),
      currentScore_medianMargin: calculateValuation(medianMargin, currentScore),
      currentScore_bestMargin: calculateValuation(bestMargin, currentScore),
      greenZone_currentMargin: calculateValuation(currentMargin, greenZoneScore),
      greenZone_medianMargin: calculateValuation(medianMargin, greenZoneScore),
      greenZone_bestMargin: calculateValuation(bestMargin, greenZoneScore),
      bestScore_currentMargin: calculateValuation(currentMargin, bestScore),
      bestScore_medianMargin: calculateValuation(medianMargin, bestScore),
      bestScore_bestMargin: calculateValuation(bestMargin, bestScore),
      currentScore,
      greenZoneScore,
      bestScore,
      currentMargin,
      medianMargin,
      bestMargin,
      ebitdaMultiple,
      currentMarginRange,
      medianMarginRange,
      bestMarginRange
    };
  };

  const matrix = calculateMatrix();

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Value Gap Analysis Report</h1>
          <div className="report-type">COMPREHENSIVE FINANCIAL EXIT PLANNING</div>
        </div>

        <div className="cover-business-info">
          <h2>{businessName}</h2>
          {industry && <p className="industry">{industry}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Value Gap Analysis Report</p>
        </div>
      </div>

      {/* Page 2: Summary, Valuation Range, Attractiveness Scores */}
      <div className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ paddingTop: '0.2in', paddingLeft: '0.5in', paddingRight: '0.5in' }}>
          {/* Summary */}
          <div style={{ marginBottom: '0.3in' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '3px solid #10b981', paddingBottom: '8px' }}>Summary</h2>
            <div style={{ backgroundColor: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '16px' }}>
              <p style={{ color: '#374151', fontSize: '14px', marginBottom: '16px', lineHeight: '1.6' }}>
                Your value gap measures the additional business value you could unlock by improving your business gap assessment score.
              </p>

              {/* Two scenarios side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Green Zone Scenario */}
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #bfdbfe' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '8px' }}>At Green Zone Score</p>
                  <p style={{ fontSize: '11px', color: '#1e40af', marginBottom: '12px' }}>({valueGap.green_zone_attractiveness?.toFixed(1)}% business gap assessment score)</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#1e40af' }}>Valuation Impact:</span>
                    <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#2563eb' }}>
                      {formatCurrency(valueGap.value_impact_green_zone)}
                    </span>
                  </div>
                </div>

                {/* Best in Class Scenario */}
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #86efac' }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#14532d', marginBottom: '8px' }}>At Best in Class Score</p>
                  <p style={{ fontSize: '11px', color: '#15803d', marginBottom: '12px' }}>({valueGap.best_in_class_attractiveness?.toFixed(1)}% business gap assessment score)</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#15803d' }}>Valuation Impact:</span>
                    <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#16a34a' }}>
                      {formatCurrency(valueGap.value_impact_best_in_class)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Valuation Range */}
          <div style={{ marginBottom: '0.3in' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '3px solid #10b981', paddingBottom: '8px' }}>Valuation Range</h2>
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Low Range:</span>
                <span style={{ fontWeight: '600', fontSize: '13px' }}>{formatCurrency(valueGap.valuation_low)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>High Range:</span>
                <span style={{ fontWeight: '600', fontSize: '13px' }}>{formatCurrency(valueGap.valuation_high)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #d1d5db' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Range Spread:</span>
                <span style={{ fontWeight: '600', fontSize: '13px' }}>{formatCurrency(valueGap.valuation_range)}</span>
              </div>
            </div>
          </div>

          {/* Attractiveness Scores */}
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px', borderBottom: '3px solid #10b981', paddingBottom: '8px' }}>Business Gap Assessment Scores</h2>
            <div style={{ backgroundColor: '#eef2ff', borderRadius: '8px', padding: '16px', border: '1px solid #c7d2fe' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Current Score</p>
                  <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#d97706' }}>{valueGap.current_attractiveness?.toFixed(1)}%</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Green Zone Score</p>
                  <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#2563eb' }}>{valueGap.green_zone_attractiveness?.toFixed(1)}%</p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Best in Class Score</p>
                  <p style={{ fontSize: '22px', fontWeight: 'bold', color: '#059669' }}>{valueGap.best_in_class_attractiveness?.toFixed(1)}%</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', paddingTop: '12px', borderTop: '1px solid #c7d2fe' }}>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Gap to Green Zone</p>
                  <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#2563eb' }}>
                    {(valueGap.green_zone_attractiveness - valueGap.current_attractiveness).toFixed(1)} percentage points
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginBottom: '4px' }}>Gap to Best in Class</p>
                  <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#16a34a' }}>
                    {(valueGap.best_in_class_attractiveness - valueGap.current_attractiveness).toFixed(1)} percentage points
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 3: Step-by-Step Calculations */}
      <div className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ paddingTop: '0.15in', paddingLeft: '0.4in', paddingRight: '0.4in' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px', borderBottom: '3px solid #10b981', paddingBottom: '6px' }}>Step-by-Step Calculations</h2>

          {/* Assessment Score Scaling Info */}
          <div style={{ backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
            <p style={{ fontSize: '12px', color: '#4338ca', fontWeight: '600', marginBottom: '3px' }}>Assessment Score Scaling (17% - 100%)</p>
            <p style={{ fontSize: '11px', color: '#4338ca' }}>
              Assessment scores range from <strong>17% (minimum)</strong> to <strong>100% (maximum)</strong>.
              A <strong>58% assessment</strong> represents an average business at approximately the <strong>midpoint (49%)</strong> of your valuation range.
            </p>
            <p style={{ fontSize: '11px', color: '#4338ca', marginTop: '3px' }}>
              Linear scaling: 17% → 0% (low), 58% → 49% (midpoint), 67% → 60% (green zone), 72% → 66% (best-in-class), 100% → 100% (high)
            </p>
          </div>

          {/* Current Value - Full Width */}
          <div style={{ marginBottom: '8px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '5px' }}>Current Exit Value</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={{ backgroundColor: '#f9fafb', borderLeft: '3px solid #6b7280', padding: '6px', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>Step 1: Scale Current Assessment Score</p>
                <p style={{ fontSize: '11px', color: '#374151' }}>
                  Position = ({valueGap.current_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '2px' }}>
                  = {(((valueGap.current_attractiveness - 17) / 83) * 100).toFixed(1)}%
                </p>
              </div>

              <div style={{ backgroundColor: '#f9fafb', borderLeft: '3px solid #6b7280', padding: '6px', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>Step 2: Calculate Current Exit Value</p>
                <p style={{ fontSize: '11px', color: '#374151' }}>
                  = {formatCurrency(valueGap.valuation_low)} + ({formatCurrency(valueGap.valuation_range)} × {(((valueGap.current_attractiveness - 17) / 83) * 100).toFixed(1)}%)
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginTop: '2px' }}>
                  = {formatCurrency(valueGap.current_exit_value)}
                </p>
              </div>
            </div>
          </div>

          {/* Two Scenarios Side by Side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {/* Left Column: Green Zone Scenario */}
            <div>
              <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '5px' }}>Scenario 1: At Green Zone ({valueGap.green_zone_attractiveness?.toFixed(1)}%)</h3>

              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '6px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 1: Scale Assessment Score</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = ({valueGap.green_zone_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {(((valueGap.green_zone_attractiveness - 17) / 83) * 100).toFixed(1)}%
                </p>
              </div>

              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '6px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 2: Calculate Green Zone Value</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = {formatCurrency(valueGap.valuation_low)} + ({formatCurrency(valueGap.valuation_range)} × {(((valueGap.green_zone_attractiveness - 17) / 83) * 100).toFixed(1)}%)
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {formatCurrency(valueGap.green_zone_value)}
                </p>
              </div>

              <div style={{ backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', padding: '6px', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a8a', marginBottom: '2px' }}>Step 3: Calculate Valuation Impact</p>
                <p style={{ fontSize: '11px', color: '#1e40af' }}>
                  = {formatCurrency(valueGap.green_zone_value)} - {formatCurrency(valueGap.current_exit_value)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e3a8a', marginTop: '2px' }}>
                  = {formatCurrency(valueGap.value_impact_green_zone)}
                </p>
              </div>
            </div>

            {/* Right Column: Best in Class Scenario */}
            <div>
              <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#14532d', marginBottom: '5px' }}>Scenario 2: At Best in Class ({valueGap.best_in_class_attractiveness?.toFixed(1)}%)</h3>

              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '6px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 1: Scale Assessment Score</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = ({valueGap.best_in_class_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {(((valueGap.best_in_class_attractiveness - 17) / 83) * 100).toFixed(1)}%
                </p>
              </div>

              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '6px', borderRadius: '4px', marginBottom: '6px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 2: Calculate Best in Class Value</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = {formatCurrency(valueGap.valuation_low)} + ({formatCurrency(valueGap.valuation_range)} × {(((valueGap.best_in_class_attractiveness - 17) / 83) * 100).toFixed(1)}%)
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {formatCurrency(valueGap.best_in_class_value)}
                </p>
              </div>

              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '3px solid #22c55e', padding: '6px', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#14532d', marginBottom: '2px' }}>Step 3: Calculate Valuation Impact</p>
                <p style={{ fontSize: '11px', color: '#15803d' }}>
                  = {formatCurrency(valueGap.best_in_class_value)} - {formatCurrency(valueGap.current_exit_value)}
                </p>
                <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#14532d', marginTop: '2px' }}>
                  = {formatCurrency(valueGap.value_impact_best_in_class)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 4: What This Means */}
      <div className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ paddingTop: '0.2in', paddingLeft: '0.5in', paddingRight: '0.5in' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px', borderBottom: '3px solid #10b981', paddingBottom: '8px' }}>What This Means</h2>

          <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ padding: '16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  <span style={{ fontWeight: '600', color: '#1e3a8a' }}>Green Zone Scenario:</span> By improving your business gap assessment score from <span style={{ fontWeight: '600' }}>{valueGap.current_attractiveness?.toFixed(1)}%</span> to
                  the green zone level of <span style={{ fontWeight: '600' }}>{valueGap.green_zone_attractiveness?.toFixed(1)}%</span>,
                  you could add <span style={{ fontWeight: '600', color: '#2563eb' }}>{formatCurrency(valueGap.value_impact_green_zone)}</span> to your exit value.
                </p>
              </div>

              <div style={{ padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  <span style={{ fontWeight: '600', color: '#14532d' }}>Best in Class Scenario:</span> By improving your business gap assessment score to
                  best-in-class level of <span style={{ fontWeight: '600' }}>{valueGap.best_in_class_attractiveness?.toFixed(1)}%</span>,
                  you could add <span style={{ fontWeight: '600', color: '#16a34a' }}>{formatCurrency(valueGap.value_impact_best_in_class)}</span> to your exit value.
                </p>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: '#374151', marginTop: '20px', lineHeight: '1.6' }}>
              Focus on improving your business gap assessment scores by addressing weaknesses in operations, financials, customer concentration, and growth potential.
              Even reaching the green zone ({valueGap.green_zone_attractiveness?.toFixed(1)}%)
              represents a {(valueGap.green_zone_attractiveness - valueGap.current_attractiveness).toFixed(1)} percentage point improvement
              and significant value creation opportunity.
            </p>
          </div>
        </div>
      </div>

      {/* Page 5: Your Valuation Journey - Matrix */}
      {matrix && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <div style={{ paddingTop: '0.15in', paddingLeft: '0.4in', paddingRight: '0.4in' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px', borderBottom: '3px solid #10b981', paddingBottom: '8px' }}>Your Valuation Journey</h2>

            {/* Valuation Scenarios Matrix */}
            <div style={{ marginBottom: '15px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', marginBottom: '4px', textAlign: 'center' }}>Valuation Scenarios Matrix</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ borderright: '2px solid #1f2937', borderbottom: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', width: '140px' }}></th>
                    <th colSpan="3" style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      EBITDA Margin
                    </th>
                  </tr>
                  <tr>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'right', fontWeight: 'bold', width: '140px' }}>
                      Assessment Score
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Current
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Median
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Best In Class
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'right' }}>
                      <strong>Current</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.currentScore_currentMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.currentScore_medianMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.currentScore_bestMargin)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'right' }}>
                      <strong>Green Zone</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.greenZone_currentMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.greenZone_medianMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.greenZone_bestMargin)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'right' }}>
                      <strong>Best In Class</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.bestScore_currentMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.bestScore_medianMargin)}
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                      {formatCurrency(matrix.bestScore_bestMargin)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Change from Baseline Matrix */}
            <div style={{ marginBottom: '15px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', marginBottom: '4px', textAlign: 'center', }}>Change from Current Baseline</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ borderright: '2px solid #1f2937', borderbottom: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', width: '140px' }}></th>
                    <th colSpan="3" style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      EBITDA Margin
                    </th>
                  </tr>
                  <tr>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#f3f4f6', padding: '4px', textAlign: 'right', fontWeight: 'bold', width: '140px' }}>
                      Assessment Score
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Current
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Median
                    </th>
                    <th style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'center', fontWeight: 'bold' }}>
                      Best In Class
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'right' }}>
                      <strong>Current</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#e5e7eb', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>$0</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>(0%)</div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.currentScore_medianMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.currentScore_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.currentScore_bestMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.currentScore_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'right' }}>
                      <strong>Green Zone</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.greenZone_currentMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.greenZone_currentMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bfdbfe', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.greenZone_medianMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.greenZone_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.greenZone_bestMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.greenZone_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'right' }}>
                      <strong>Best In Class</strong>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.bestScore_currentMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.bestScore_currentMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: 'white', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.bestScore_medianMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.bestScore_medianMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                    <td style={{ border: '2px solid #1f2937', backgroundColor: '#bbf7d0', padding: '4px', textAlign: 'center', fontWeight: '600' }}>
                      <div>{formatCurrency(matrix.bestScore_bestMargin - matrix.currentScore_currentMargin)}</div>
                      <div style={{ fontSize: '12px', color: '#059669' }}>
                        (+{(((matrix.bestScore_bestMargin - matrix.currentScore_currentMargin) / matrix.currentScore_currentMargin) * 100).toFixed(1)}%)
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', marginTop: '4px' }}>
                All changes are calculated relative to the Current/Current baseline value of {formatCurrency(matrix.currentScore_currentMargin)}
              </p>
            </div>

            {/* Matrix Parameters and Valuation Ranges */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '12px', color: '#4b5563' }}>
              {/* Matrix Parameters */}
              <div>
                <p style={{ marginBottom: '4px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #4b5563' }}>Matrix Parameters:</p>
                <ul style={{ margin: 0, paddingLeft: '16px', lineHeight: '2' }}>
                  <li><strong>Industry EV/EBITDA Multiple:</strong> {matrix.ebitdaMultiple.toFixed(2)}x</li>
                  <li><strong>Current EBITDA Margin:</strong> {matrix.currentMargin.toFixed(1)}%</li>
                  <li><strong>Median Industry EBITDA Margin:</strong> {matrix.medianMargin.toFixed(1)}%</li>
                  <li><strong>Best In Class Industry EBITDA Margin:</strong> {matrix.bestMargin.toFixed(1)}%</li>
                  <li><strong>Current Overall Assessment Score:</strong> {matrix.currentScore.toFixed(0)}%</li>
                  <li><strong>Green Zone Assessment Score:</strong> {matrix.greenZoneScore}%</li>
                  <li><strong>Best In Class Assessment Score:</strong> {matrix.bestScore}%</li>
                </ul>
              </div>

              {/* Valuation Ranges */}
              <div>
                <p style={{ marginBottom: '4px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #4b5563' }}>Valuation Ranges by EBITDA Margin:</p>
                <ul style={{ margin: 0, paddingLeft: '16px', lineHeight: '2' }}>
                  <li>
                    <strong>Current Margin ({matrix.currentMargin.toFixed(1)}%):</strong><br />
                    {formatCurrency(matrix.currentMarginRange.low)} - {formatCurrency(matrix.currentMarginRange.high)}
                  </li>
                  <li>
                    <strong>Median Margin ({matrix.medianMargin.toFixed(1)}%):</strong><br />
                    {formatCurrency(matrix.medianMarginRange.low)} - {formatCurrency(matrix.medianMarginRange.high)}
                  </li>
                  <li>
                    <strong>Best In Class Margin ({matrix.bestMargin.toFixed(1)}%):</strong><br />
                    {formatCurrency(matrix.bestMarginRange.low)} - {formatCurrency(matrix.bestMarginRange.high)}
                  </li>
                </ul>
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', marginTop: '8px' }}>
              Calculations use Value Gap framework: Position = (Score - 17) / 83 × 100; Valuation = Low + (Range × Position)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintValueGapDetails;
